import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Button from '../../shared/button.component';
import SecondaryMenu from '../../menu/secondaryMenu.component';
import NoDataToDisplay from '../../shared/noDataToDisplay.component';
import Select from '../../shared/reactSelect/select.component';
import PeriodDropdown from '../../shared/periodDropdown.component';
import { withPermissions, permissions } from '../../shared/authorization';

import HamburgerDropdown from './hamburgerDropdown.component';
import CategoriesAndTagsGrid from './categoriesAndTagsGrid.component';
import AddCategoriesModal from './addCategoriesModal.component';
import DeleteCategoriesModal from './deleteCategoriesModal.component';

import ModalNames from './modalNames';
import AddTagModal from './addTagModal.component';
import DeleteTagModal from './deleteTagModal.component';
import RefusedDeletingModal from './refusedDeletingModal.component';

import { modalDataSelector } from '../../shared/modals/modals.selectors';

import {
  showTagsModalForAddEdit,
  selectCategoryForCategoriesAndTags,
  fetchCategoriesForCategoriesAndTags,
  hideTagsModalForRefusedDeleting,
  hideTagsModalForDelete,
  fetchTagsForCategoriesAndTags,
  fetchSubTagsForCategoriesAndTags,
  hideCategoryModalForRefusedDeleting,
  uploadTags,
  importTags,
  deleteTag,
  clearImportedTagsEntities,
} from './store/actions';

import {
  fetchTagsErrorSelector,
  fetchTagsLoadingSelector,
  fetchSubTagsErrorSelector,
  fetchSubTagsLoadingSelector,
  categoriesOptionsSelector,
  tagsListSelector,
  subTagsListSelector,
  canSeeAdditionalColumnsSelector,
  isUploadingTagsSelector,
  isImportingTagsSelector,
  uploadedTagsSelector,
  areImportsPendingSelector,
  isDeletingTagInProgressSelector,
  canModifyCurrentCategory,
  selectedCategorySelector,
} from './store/selectors';

import {
  periodsSelector,
  periodOrCurrentSelector,
  environmentSuffixSelector,
} from '../../shared/store/selectors';

import { subTagsPropTypes, tagsPropTypes } from './propTypes';
import config from '../../config';
import downloadTagsXlsx from './downloadTagsXlsx';
import NavigationPrompt, { IMPORTED_DATA_PENDING_MESSAGE } from '../../shared/navigationPrompt.component';
import AdminEntityImport from '../shared/adminEntityImport.component';
import { importColumnDefinitions } from './columnDefinitions';
import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../userData/store/selectors';
import { PeriodPropTypes } from '../shared/propTypes';
import { ReduxAlert } from '../../shared/alert.component';
import { ViewImportTabs } from '../shared/ViewImportTabs.component';

const TabTypes = {
  VIEW: 'VIEW',
  IMPORT: 'IMPORT',
};

const categoryModalDataPropType = PropTypes.shape({
  show: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    categoryName: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    catView: PropTypes.string.isRequired,
    defaultTag: PropTypes.string,
  }),
});

const tagModalDataPropType = PropTypes.shape({
  show: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    tagName: PropTypes.string.isRequired,
  }),
});

class CategoriesAndTagsPage extends PureComponent {
  static propTypes = {
    canModifyInSelectedPeriod: PropTypes.bool.isRequired,
    additionalPermissions: PropTypes.shape({
      modifyCategories: PropTypes.shape({
        permission: PropTypes.bool.isRequired,
        permissionInSelectedPeriod: PropTypes.bool.isRequired,
      }),
    }),

    selectCategoryForCategoriesAndTags: PropTypes.func.isRequired,
    showTagsModalForAddEdit: PropTypes.func.isRequired,
    fetchCategoriesForCategoriesAndTags: PropTypes.func.isRequired,
    fetchTagsForCategoriesAndTags: PropTypes.func.isRequired,
    fetchSubTagsForCategoriesAndTags: PropTypes.func.isRequired,
    hideTagsModalForRefusedDeleting: PropTypes.func.isRequired,
    hideTagsModalForDelete: PropTypes.func.isRequired,
    hideCategoryModalForRefusedDeleting: PropTypes.func.isRequired,

    addEditCategoriesModalData: categoryModalDataPropType,
    deleteCategoryModalData: categoryModalDataPropType,
    refuseDeletingCategoryModalData: categoryModalDataPropType,

    fetchTagsError: PropTypes.string.isRequired,
    fetchTagsLoading: PropTypes.bool,

    fetchSubTagsError: PropTypes.string,
    fetchSubTagsLoading: PropTypes.bool,

    period: PeriodPropTypes,

    periods: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      taxYear: PropTypes.string.isRequired,
    })).isRequired,

    categoriesList: PropTypes.arrayOf(PropTypes.shape({
      categoryName: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      catView: PropTypes.string.isRequired,
      defaultTag: PropTypes.string,
      subCategoryName: PropTypes.string,
    })).isRequired,

    categoriesOptions: PropTypes.arrayOf(PropTypes.shape({
      categoryName: PropTypes.string,
      description: PropTypes.string,
      catView: PropTypes.string,
      defaultTag: PropTypes.string,
      subCategoryName: PropTypes.string,
    })).isRequired,

    category: PropTypes.shape({
      categoryName: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      catView: PropTypes.string.isRequired,
      defaultTag: PropTypes.string,
      subCategoryName: PropTypes.string,
    }),

    canModifyCurrentCategory: PropTypes.bool,

    tagsList: PropTypes.arrayOf(tagsPropTypes),
    subTagsList: PropTypes.arrayOf(subTagsPropTypes),

    categoryViews: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })),

    addEditTagsModalData: tagModalDataPropType,
    deleteTagModalData: tagModalDataPropType,
    refuseDeletingTagModalData: tagModalDataPropType,
    canSeeAdditionalColumns: PropTypes.bool.isRequired,

    environmentSuffix: PropTypes.string.isRequired,
    deleteTag: PropTypes.func.isRequired,
    isDeletingTagInProgress: PropTypes.bool.isRequired,
    uploadTags: PropTypes.func.isRequired,
    importTags: PropTypes.func.isRequired,
    isUploadingTags: PropTypes.bool,
    uploadedTags: PropTypes.arrayOf(tagsPropTypes),
    clearImportedTagsEntities: PropTypes.func.isRequired,

    isImportingTags: PropTypes.bool,
    areImportsPending: PropTypes.bool,

    shouldDisplayZero: PropTypes.bool.isRequired,
    hideDecimals: PropTypes.bool.isRequired,
    negativeMinus: PropTypes.bool.isRequired,
  };

  state = {
    activeTab: TabTypes.VIEW,
  };


  componentDidMount() {
    const { fetchCategoriesForCategoriesAndTags, period } = this.props;

    fetchCategoriesForCategoriesAndTags(period);
  }

  componentWillUnmount() {
    this.props.clearImportedTagsEntities();
  }

  selectPeriod = selectedPeriod => {
    const {
      periods,
      fetchCategoriesForCategoriesAndTags,
      fetchTagsForCategoriesAndTags,
      category,
    } = this.props;

    const value = selectedPeriod ? selectedPeriod.value : null;
    fetchCategoriesForCategoriesAndTags(periods.find(period => period.name === value));
    if (category) {
      fetchTagsForCategoriesAndTags(value, category.categoryName);
      fetchSubTagsForCategoriesAndTags(value, category.subCategoryName);
    }
  };

  selectCategory = selectedCategory => {
    const { categoriesList, selectCategoryForCategoriesAndTags, period } = this.props;

    const value = selectedCategory ? selectedCategory.value : null;
    selectCategoryForCategoriesAndTags(period,
      categoriesList.find(category => category.categoryName === value));
  };

  showTagsModalForAdd = () =>
    this.props.showTagsModalForAddEdit();

  changeTab = activeTab => this.setState({ activeTab });

  generateTemplateName = () => `TLS_Tags_${this.props.period.name}${this.props.environmentSuffix}.xlsx`;

  exportToExcel = callback => {
    const {
      period,
      tagsList,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      environmentSuffix,
      categoryViews,
      category,
    } = this.props;

    downloadTagsXlsx({
      periodName: period.name,
      categoryDescription: category ? category.description : '',
      categoryViews,
      tagsList,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      environmentSuffix,
    });

    if (callback) {
      callback();
    }
  };

  uploadImportFile = file => {
    const { uploadTags, period, category } = this.props;

    uploadTags({
      file, period, category,
    });
  };

  fileToImportSelect = hideDropdown => () => {
    hideDropdown();
    this.changeTab(TabTypes.IMPORT);
  };

  fileToImportChange = event => {
    this.uploadImportFile(event.target.files[0]);
  };

  importTags = async tags => {
    const { importTags, period, category, fetchTagsForCategoriesAndTags } = this.props;

    await importTags({ tags, period, category });

    this.changeTab(TabTypes.VIEW);
    fetchTagsForCategoriesAndTags(period.name, category.categoryName);
  };

  renderViewGrid = () => {
    const {
      canModifyInSelectedPeriod,
      tagsList,
      fetchTagsLoading,
      subTagsList,
      fetchSubTagsLoading,
      canSeeAdditionalColumns,
      category,
      canModifyCurrentCategory,
    } = this.props;

    const selectedCategory = category ? category.categoryName : '';

    return (
      <CategoriesAndTagsGrid
        componentToRenderOnSearchBar={
          selectedCategory && canModifyInSelectedPeriod && canModifyCurrentCategory ? (
            <Button
              kind="primary"
              onClick={this.showTagsModalForAdd}
            >
              Add Tag
            </Button>
          ) : null
        }
        tagsList={tagsList}
        tagsLoading={fetchTagsLoading}
        subTagsList={subTagsList}
        subTagsLoading={fetchSubTagsLoading}
        shouldDisplayAdminColumn={canModifyInSelectedPeriod && canModifyCurrentCategory}
        canSeeAdditionalColumns={canSeeAdditionalColumns}
      />
    );
  };

  renderImportView = () => {
    const {
      tagsList,
      uploadedTags,
      isUploadingTags,
    } = this.props;

    return (
      <AdminEntityImport
        className="ag-grid-full-page-with-secondary-menu-and-tabs"
        uploadingEntities={isUploadingTags}
        uploadedEntities={uploadedTags}
        pageTitle={'Importing Tags'}
        entities={tagsList}
        idField={'tagName'}
        templatePath={config.templates.CATEGORIES_AND_TAGS}
        templateFileName={this.generateTemplateName()}
        columnDefs={importColumnDefinitions}
        selectFileToUpload={this.uploadImportFile}
        importEntities={this.importTags}
        ignoredFields={['catView', 'defaultTag']}
      />
    );
  };

  render() {
    const {
      canModifyInSelectedPeriod,
      additionalPermissions: {
        modifyCategories: {
          permissionInSelectedPeriod: canModifyCategories,
        },
      },
      category,
      fetchTagsError,
      period,
      categoriesOptions,
      refuseDeletingTagModalData,
      hideTagsModalForRefusedDeleting,
      hideTagsModalForDelete,
      addEditCategoriesModalData,
      deleteCategoryModalData,
      addEditTagsModalData,
      deleteTagModalData,
      refuseDeletingCategoryModalData,
      hideCategoryModalForRefusedDeleting,
      areImportsPending,
      isDeletingTagInProgress,
      deleteTag,
      canModifyCurrentCategory,
    } = this.props;

    const {
      activeTab,
    } = this.state;

    const selectedCategory = category ? category.categoryName : '';
    const description = category ? category.description : '';
    const catView = category ? category.catView : null;
    const defaultTag = category ? category.defaultTag : null;

    return (
      <div className="categories-and-tags-page">
        <NavigationPrompt
          show={areImportsPending}
          message={<span>{IMPORTED_DATA_PENDING_MESSAGE}</span>}
        />
        <SecondaryMenu>
          <PeriodDropdown
            onChange={this.selectPeriod}
          />
          <Select
            className="select-categories"
            clearable={false}
            options={categoriesOptions}
            value={selectedCategory}
            onChange={this.selectCategory}
            virtualized
          />
          <div className="hamburger-dropdown ml-auto text-right">
            <HamburgerDropdown
              period={period}
              category={category}
              disabledActions={{
                categoryNotSelected: !selectedCategory || selectedCategory.trim() === '-',
              }}
              canModify={canModifyInSelectedPeriod}
              canModifyCategories={canModifyCategories}
              canModifyCurrentCategory={canModifyCurrentCategory}
              exportToExcel={this.exportToExcel}
              templatePath={config.templates.CATEGORIES_AND_TAGS}
              templateFileName={this.generateTemplateName()}
              fileToImportChange={this.fileToImportChange}
              fileToImportSelect={this.fileToImportSelect}
            />
          </div>
        </SecondaryMenu>
        <div className="page admin-import-page">
          <div className="container-fluid">
            <ReduxAlert/>
            <div className="row no-gutters page-content-container">
              <div className="col">
                <div className="d-flex flex-row justify-content-between">
                  <h5>Categories and Tags</h5>
                </div>
              </div>
            </div>
            <div className="row no-gutters page-content-container">
              <div className="col">
                {
                  !selectedCategory
                  && <NoDataToDisplay
                    header="No Category Selected"
                    details="Please select a category to view."
                  />
                }
                {selectedCategory && fetchTagsError && <NoDataToDisplay header={fetchTagsError}/>}
                {selectedCategory && !fetchTagsError && (
                  <ViewImportTabs
                    canModify={canModifyInSelectedPeriod && canModifyCurrentCategory}
                    activeTab={activeTab}
                    changeTab={this.changeTab}
                    renderViewTab={this.renderViewGrid}
                    renderImportTab={this.renderImportView}
                    viewTabOnly={!canModifyInSelectedPeriod}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <RefusedDeletingModal
          message="This tag cannot be deleted because it is associated with an account or the default tag for this category."
          hideModal={hideTagsModalForRefusedDeleting}
          isVisible={refuseDeletingTagModalData.show}
        />
        <DeleteTagModal
          entityToRemove={deleteTagModalData.data}
          periodName={period.name}
          categoryName={selectedCategory}
          isVisible={deleteTagModalData.show}
          hideModal={hideTagsModalForDelete}
          isDeletingInProgress={isDeletingTagInProgress}
          removeEntity={deleteTag}
        />
        <AddTagModal
          period={period}
          category={selectedCategory}
          entityToEdit={addEditTagsModalData && addEditTagsModalData.data}
          openForEdit={Boolean(addEditTagsModalData && addEditTagsModalData.data)}
          isVisible={addEditTagsModalData.show}
        />
        <AddCategoriesModal
          period={period}
          entityToEdit={addEditCategoriesModalData.data}
          isVisible={addEditCategoriesModalData.show}
          openForEdit={Boolean(addEditCategoriesModalData && addEditCategoriesModalData.data)}
        />
        <DeleteCategoriesModal
          period={period}
          categoryName={selectedCategory}
          description={description}
          catView={catView}
          defaultTag={defaultTag}
          isVisible={deleteCategoryModalData.show}
        />
        <RefusedDeletingModal
          message="Delete category failed"
          hideModal={hideCategoryModalForRefusedDeleting}
          isVisible={refuseDeletingCategoryModalData.show}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { administration } = state;
  return {
    environmentSuffix: environmentSuffixSelector(state),
    categoriesList: administration.categoriesAndTags.categoriesList,
    categoryViews: administration.categoriesAndTags.categoryViews,
    category: selectedCategorySelector(state),
    canModifyCurrentCategory: canModifyCurrentCategory(state),
    tagsList: tagsListSelector(state),
    subTagsList: subTagsListSelector(state),
    fetchTagsError: fetchTagsErrorSelector(state),
    fetchTagsLoading: fetchTagsLoadingSelector(state),
    fetchSubTagsError: fetchSubTagsErrorSelector(state),
    fetchSubTagsLoading: fetchSubTagsLoadingSelector(state),
    period: periodOrCurrentSelector(state),
    periods: periodsSelector(state),
    categoriesOptions: categoriesOptionsSelector(state),
    addEditCategoriesModalData: modalDataSelector(state, ModalNames.CategoryAddEdit),
    deleteCategoryModalData: modalDataSelector(state, ModalNames.CategoryDelete),
    refuseDeletingCategoryModalData: modalDataSelector(state, ModalNames.CategoryDeleteRefused),
    addEditTagsModalData: modalDataSelector(state, ModalNames.TagAddEdit),
    deleteTagModalData: modalDataSelector(state, ModalNames.TagDelete),
    refuseDeletingTagModalData: modalDataSelector(state, ModalNames.TagDeleteRefused),
    canSeeAdditionalColumns: canSeeAdditionalColumnsSelector(state),
    isUploadingTags: isUploadingTagsSelector(state),
    isImportingTags: isImportingTagsSelector(state),
    uploadedTags: uploadedTagsSelector(state),
    areImportsPending: areImportsPendingSelector(state),
    shouldDisplayZero: displayZeroSelector(state),
    hideDecimals: hideDecimalsSelector(state),
    negativeMinus: negativeMinusSelector(state),
    isDeletingTagInProgress: isDeletingTagInProgressSelector(state),
  };
}

export default withPermissions(permissions.CATEGORIES_AND_TAGS)(connect(
  mapStateToProps,
  {
    selectCategoryForCategoriesAndTags,
    showTagsModalForAddEdit,
    fetchCategoriesForCategoriesAndTags,
    hideTagsModalForRefusedDeleting,
    hideTagsModalForDelete,
    fetchTagsForCategoriesAndTags,
    fetchSubTagsForCategoriesAndTags,
    hideCategoryModalForRefusedDeleting,
    uploadTags,
    importTags,
    deleteTag,
    clearImportedTagsEntities,
  }
)(CategoriesAndTagsPage));
